import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectMilestoneCoordinationApi from "../../../../../infrastructure/api/projectMilestoneCoordination";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const boolChip = (value, yesLabel = "بله", noLabel = "خیر") =>
  value ? (
    <Chip size="small" color="success" icon={<CheckCircleOutlineIcon />} label={yesLabel} />
  ) : (
    <Chip size="small" color="default" icon={<HighlightOffIcon />} label={noLabel} />
  );

const statusColor = (title) => {
  const text = String(title || "");
  if (text.includes("آماده تکمیل")) return "success";
  if (text.includes("آماده شروع")) return "primary";
  if (text.includes("انتظار پرداخت") || text.includes("انتظار تأمین")) return "warning";
  if (text.includes("مسدود") || text.includes("لغو")) return "error";
  if (text.includes("تکمیل")) return "info";
  if (text.includes("در حال انجام")) return "info";
  return "default";
};

const ProjectMilestoneCoordinationPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [planId, setPlanId] = useState("1");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadData = useCallback(async () => {
    const pid = planId.trim() ? Number(planId.trim()) : 0;
    if (!pid) {
      setError("شناسه برنامه را وارد کنید.");
      return;
    }
    setLoading(true);
    setError("");
    const res = await ProjectMilestoneCoordinationApi.getCoordinationByPlan(source, pid);
    setLoading(false);
    if (!res.ok) {
      setError(res.message || "خطا در دریافت هماهنگی مایلستون‌ها");
      setRows([]);
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [planId, source]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEvaluate = async () => {
    const pid = planId.trim() ? Number(planId.trim()) : 0;
    if (!pid) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectMilestoneCoordinationApi.evaluatePlanCoordination(source, pid);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.message || "خطا در ارزیابی هماهنگی");
      return;
    }
    setSuccess("هماهنگی تمام مایلستون‌های برنامه ارزیابی شد.");
    await loadData();
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent
        items={[
          { title: "سولارنت", link: basePath },
          { title: areaLabel, link: basePath },
          { title: "هماهنگی مایلستون‌ها" },
        ]}
      />

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="هماهنگی مایلستون‌ها"
          subheader="وضعیت هماهنگی Payment/Procurement با مایلستون‌های برنامه اجرا (فاز ۱۷)"
          action={
            <Stack direction="row" spacing={1}>
              <IconButton onClick={loadData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={handleEvaluate}
                disabled={submitting || loading}
              >
                ارزیابی برنامه
              </Button>
            </Stack>
          }
        />
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="شناسه برنامه اجرا (PlanId)"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button variant="outlined" onClick={loadData} disabled={loading}>
              بارگذاری
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مایلستون</TableCell>
                  <TableCell>وضعیت مایلستون</TableCell>
                  <TableCell>محرک‌ها</TableCell>
                  <TableCell>هماهنگی</TableCell>
                  <TableCell>پرداخت</TableCell>
                  <TableCell>تأمین</TableCell>
                  <TableCell>CanStart</TableCell>
                  <TableCell>CanComplete</TableCell>
                  <TableCell>دلیل مسدودی</TableCell>
                  <TableCell>آخرین ارزیابی</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {loading ? "در حال بارگذاری..." : "رکوردی یافت نشد."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.projectExecutionPlanMilestoneId || row.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.milestoneTitle || `#${row.projectExecutionPlanMilestoneId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{row.projectExecutionPlanMilestoneId}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.milestoneStatusTitle || "-"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {row.isPaymentTrigger && <Chip size="small" label="پرداخت" />}
                        {row.isProcurementTrigger && <Chip size="small" label="تأمین" />}
                        {!row.isPaymentTrigger && !row.isProcurementTrigger && (
                          <Typography variant="caption">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(row.coordinationStatusTitle)}
                        label={row.coordinationStatusTitle || "-"}
                      />
                    </TableCell>
                    <TableCell>
                      {row.hasPaymentActivation
                        ? row.paymentActivationStatusTitle || row.paymentActivationStatus
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {row.hasProcurementActivation
                        ? row.procurementActivationStatusTitle || row.procurementActivationStatus
                        : "—"}
                    </TableCell>
                    <TableCell>{boolChip(row.canStartMilestone)}</TableCell>
                    <TableCell>{boolChip(row.canCompleteMilestone)}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{row.blockReason || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.lastEvaluatedAt
                        ? toJalaliFullDate(row.lastEvaluatedAt)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectMilestoneCoordinationPage;
